'use client';

import { lazy, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { DesktopSearchBar } from '#/components/DesktopSearchBar/DesktopSearchBar';
import { DesktopSearchBarLocationInput } from '#/components/DesktopSearchBar/DesktopSearchBarLocationInput';
import { DesktopSearchBarQueryInput } from '#/components/DesktopSearchBar/DesktopSearchBarQueryInput';
import { DesktopSearchBarSearchTypeSelect } from '#/components/DesktopSearchBar/DesktopSearchBarSearchTypeSelect';
import { DesktopSearchBarSubmitButton } from '#/components/DesktopSearchBar/DesktopSearchBarSubmitButton';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { Icon } from '#/components/Icon/Icon';
import {
  LocationRadiusSelect,
  type LocationRadiusSelectHandle,
} from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { MobileSearchMenu } from '#/components/MobileSearchMenu/MobileSearchMenu';
import { RouteLoadingProgressBar } from '#/components/RouteLoadingProgressBar/RouteLoadingProgressBar';
import { FlashesContainer } from '#/containers/FlashesContainer';
import { useEnvironment, useUser } from '#/contexts/UserEnvironmentContext';
import { useCombinedRouterMatchesHandle } from '#/hooks/useCombinedRouterMatchesHandle';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import {
  authLoginRoute,
  authSignupRoute,
} from '#/modules/auth/routing/auth.routes';
import { useSearchRadius } from '#/modules/search/contexts/SearchContext';
import { isPointGeotype } from '#/modules/search/helpers/isPointGeotype';
import { useSearchRadiusOptions } from '#/modules/search/hooks/facets/useSearchRadiusOptions';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import { searchGetVolopsRoutePath } from '#/modules/search/volops/routing/searchGetVolopsRoutePath';
import { homeRoute } from '#/routing/routes';
import { showModal } from '#/zustand-stores/modalStore';

import { LayoutHeaderButton } from './Button/LayoutHeaderButton';
import { ApiAndIntegrationsDropdown } from './dropdowns/api-and-integrations/ApiAndIntegrationsDropdown';
import { FindAJobDropdown } from './dropdowns/find-a-job/FindAJobDropdown';
import { ParticipateDropdown } from './dropdowns/participate/ParticipateDropdown';
import { PostOnIdealistDropdown } from './dropdowns/post-on-idealist/PostOnIdealistDropdown';
import { VolunteerDropdown } from './dropdowns/volunteer/VolunteerDropdown';
import {
  LayoutHeaderApiAndIntegrationsDropdownHolder,
  LayoutHeaderAuthHolder,
  LayoutHeaderAuthLink,
  LayoutHeaderBottomSectionHolder,
  LayoutHeaderDesktopDonateLinkHolder,
  LayoutHeaderDesktopSearchHolder,
  LayoutHeaderHolder,
  LayoutHeaderLeft,
  LayoutHeaderLinksHolder,
  LayoutHeaderLogoLink,
  LayoutHeaderMobileSearchHolder,
  LayoutHeaderProgressBarHolder,
  LayoutHeaderRight,
  LayoutHeaderSignupLinkHolder,
  LayoutHeaderTopSectionHolder,
  SearchRefinementsHolder,
} from './LayoutHeader.styled';
import { LayoutHeaderDefaultSearchTypeContext } from './LayoutHeaderDefaultSearchTypeContext';
import { MobileSearchV2 } from './MobileSearchV2/MobileSearchV2';
import { useIsMobileSearchV2Enabled } from './MobileSearchV2/useIsMobileSearchV2Enabled';
import { useLayoutHeaderSearch } from './useLayoutHeaderSearch';

const LazyLayoutHeaderLoggedInRightItems = lazy(() =>
  import('./LayoutHeaderLoggedInRightItems').then((m) => ({
    default: m.LayoutHeaderLoggedInRightItems,
  })),
);

const DONATE_BUTTON_DETAILS = {
  en: {
    text: 'Donate',
    href: 'https://giving.classy.org/campaign/673245/donate',
  },
  pt: {
    text: 'Doar',
    href: '/pt/doacao',
  },
  es: {
    text: 'Haz una Donación',
    href: '/es/dona',
  },
} as const satisfies Record<I18nLocale, { text: string; href: string }>;

const IDEALIST_DAYS_PATH = {
  en: '/en/days/get-involved',
  es: '/es/dias/participa',
  pt: '/pt/days/participe',
};

export function LayoutHeader() {
  const {
    defaultSearchType = 'JOB',
    searchFromPageType,
    showDesktopSearch = false,
    isSearchPage = false,
    isSearchLandingPage = false,
    showSearchRefinements = false,
    searchRefinements,
  } = useCombinedRouterMatchesHandle<LayoutHandleProps>();

  const location = useLocation();
  const redirectTo = `${location.pathname}${location.search}`;

  const { i18n, locale } = useI18n();
  const { user } = useUser();

  const { environment } = useEnvironment();
  // Note: once we migrate to horizon we can remove this check as `environment`
  // is loaded during SSR and always present
  const isUserLoaded = Boolean(environment);

  const [withScrolledTransition, setWithScrolledTransition] =
    useState(isSearchPage);
  useEffect(() => {
    if (isSearchPage === withScrolledTransition) return;
    setWithScrolledTransition(isSearchPage);
  }, [isSearchPage, withScrolledTransition]);

  const radius = useSearchRadius();
  const { setSearchUrlRadius } = useSearchPageActions({ isSearchLandingPage });

  const radiusOptions = useSearchRadiusOptions();

  const radiusHandle: LocationRadiusSelectHandle = isSearchPage
    ? { type: 'controlled', onChange: setSearchUrlRadius, radius }
    : { type: 'uncontrolled', radius };

  const isNewMobileSearch = useIsMobileSearchV2Enabled();

  const {
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear,
  } = useLayoutHeaderSearch({
    defaultSearchType,
    isSearchPage,
    isSearchLandingPage,
    embedSearchWidgetId: null,
    searchFromPageType,
  });

  return (
    <LayoutHeaderHolder id="layout-header" data-qa-id="page-header">
      <LayoutHeaderTopSectionHolder>
        <LayoutHeaderLeft>
          <LayoutHeaderLogoLink
            type="link"
            href={homeRoute.with({ lang: locale })}
            title="Idealist"
            trackClicked={{ name: 'Idealist Logo Header' }}
            qaId="header-logo-link"
          >
            <IdealistLogo withText height={{ mobile: 28, desktop: 36 }} />
          </LayoutHeaderLogoLink>

          <LayoutHeaderLinksHolder $isInSearchPage={isSearchPage}>
            {locale === I18nLocale.ES || locale === I18nLocale.PT ? (
              <ParticipateDropdown locale={locale} />
            ) : (
              <LayoutHeaderButton
                variant="grey"
                data={{
                  type: 'link',
                  href: IDEALIST_DAYS_PATH[locale],
                  trackClicked: {
                    name: 'Idealist Days',
                    properties: { context: 'Header' },
                  },
                }}
              >
                Get Involved
              </LayoutHeaderButton>
            )}

            <FindAJobDropdown />

            {[I18nLocale.EN, I18nLocale.ES].includes(locale) ? (
              <VolunteerDropdown />
            ) : (
              <LayoutHeaderButton
                variant="grey"
                qaId="nav-volop-link"
                data={{
                  type: 'link',
                  href: searchGetVolopsRoutePath({
                    locale,
                    embedSearchWidgetId: null,
                  }),
                  trackClicked: {
                    name: 'Volunteer',
                    properties: { context: 'Header' },
                  },
                }}
              >
                {locale === I18nLocale.PT ? 'Voluntariar' : i18n('Volunteer')}
              </LayoutHeaderButton>
            )}

            <PostOnIdealistDropdown />

            {locale === I18nLocale.EN && (
              <LayoutHeaderApiAndIntegrationsDropdownHolder>
                <ApiAndIntegrationsDropdown />
              </LayoutHeaderApiAndIntegrationsDropdownHolder>
            )}

            {user?.orgs.length === 0 && (
              <LayoutHeaderDesktopDonateLinkHolder>
                <LayoutHeaderButton
                  variant="grey"
                  data={{
                    type: 'link',
                    href: DONATE_BUTTON_DETAILS[locale].href,
                    trackClicked: {
                      name: 'Donate',
                      properties: { context: 'Header' },
                    },
                  }}
                >
                  {DONATE_BUTTON_DETAILS[locale].text}
                </LayoutHeaderButton>
              </LayoutHeaderDesktopDonateLinkHolder>
            )}
          </LayoutHeaderLinksHolder>
        </LayoutHeaderLeft>

        <LayoutHeaderRight>
          {isUserLoaded && !user && (
            <LayoutHeaderAuthHolder>
              <LayoutHeaderAuthLink
                type="link"
                href={authLoginRoute.with({ to: redirectTo })}
                rel="nofollow"
                trackClicked={{
                  name: 'Log In Header',
                }}
                onClick={(event) => {
                  event.preventDefault();

                  showModal('LOGIN', {
                    redirectTo,
                    manualLogin: true,
                  });
                }}
                qaId="login-link"
                aria-label={i18n('Log In')}
              >
                {i18n('Log In')}
              </LayoutHeaderAuthLink>

              <LayoutHeaderSignupLinkHolder>
                <LayoutHeaderAuthLink
                  type="link"
                  href={authSignupRoute.with({})}
                  rel="nofollow"
                  trackClicked={{
                    name: 'Signup Header',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    showModal('SIGNUP', {});
                  }}
                  qaId="signup-link"
                  aria-label={i18n('Sign Up')}
                >
                  {i18n('Sign Up')}
                </LayoutHeaderAuthLink>
              </LayoutHeaderSignupLinkHolder>
            </LayoutHeaderAuthHolder>
          )}

          {!isNewMobileSearch && (
            <LayoutHeaderMobileSearchHolder>
              <Dropdown
                summaryAriaLabel={i18n('Search')}
                trigger={
                  <LayoutHeaderButton
                    data={{ type: 'inert' }}
                    ariaLabel={i18n('Search')}
                    qaId="search-menu-button"
                    variant="grey"
                  >
                    <Icon name="search" size={24} />
                  </LayoutHeaderButton>
                }
                trackClickedOpen={{
                  name: 'Header Utility CTA - Search',
                }}
              >
                <MobileSearchMenu
                  defaultSearchType={defaultSearchType}
                  initialQuery={initialQuery}
                  searchLocation={searchLocation}
                  onChangeSearchLocation={changeSearchLocation}
                  radiusOptions={radiusOptions}
                  radiusHandle={radiusHandle}
                  onSearch={search}
                  searchOnClear={searchOnClear}
                />
              </Dropdown>
            </LayoutHeaderMobileSearchHolder>
          )}

          {user && <LazyLayoutHeaderLoggedInRightItems user={user} />}
        </LayoutHeaderRight>
      </LayoutHeaderTopSectionHolder>

      <LayoutHeaderBottomSectionHolder
        $isInSearchPage={isSearchPage}
        $withScrolledTransition={isSearchPage && withScrolledTransition}
      >
        {showDesktopSearch && (
          <LayoutHeaderDesktopSearchHolder data-qa-id="page-header-search-menu">
            <DesktopSearchBar
              defaultSearchType={defaultSearchType}
              onSearch={search}
              searchTypeSelect={<DesktopSearchBarSearchTypeSelect />}
              locationInput={
                <DesktopSearchBarLocationInput
                  searchLocation={searchLocation ?? null}
                  onChangeSearchLocation={changeSearchLocation}
                />
              }
              radiusInput={
                <LocationRadiusSelect
                  handle={radiusHandle}
                  options={radiusOptions}
                  disabled={
                    !searchLocation || !isPointGeotype(searchLocation.geoType)
                  }
                  qaId="page-header-desktop-location-radius"
                />
              }
              queryInput={
                <DesktopSearchBarQueryInput
                  initialQuery={initialQuery}
                  searchOnClear={searchOnClear}
                />
              }
              submitButton={<DesktopSearchBarSubmitButton />}
            />
          </LayoutHeaderDesktopSearchHolder>
        )}

        {isNewMobileSearch && (
          <MobileSearchV2
            defaultSearchType={defaultSearchType}
            isSearchPage={isSearchPage}
            isSearchLandingPage={isSearchLandingPage}
            initialQuery={initialQuery}
            searchLocation={searchLocation}
            onChangeSearchLocation={changeSearchLocation}
            radiusOptions={radiusOptions}
            radiusHandle={radiusHandle}
            onSearch={search}
            searchOnClear={searchOnClear}
          />
        )}

        {showSearchRefinements && (
          <SearchRefinementsHolder $hideOnMobile={isNewMobileSearch}>
            <LayoutHeaderDefaultSearchTypeContext value={defaultSearchType}>
              {searchRefinements}
            </LayoutHeaderDefaultSearchTypeContext>
          </SearchRefinementsHolder>
        )}
        <FlashesContainer />

        <LayoutHeaderProgressBarHolder>
          <RouteLoadingProgressBar />
        </LayoutHeaderProgressBarHolder>
      </LayoutHeaderBottomSectionHolder>
    </LayoutHeaderHolder>
  );
}
